// DashboardMap — Phase 4 Plan 04-05 (DIS-14, DIS-15, UI-SPEC §Screen 1).
//
// HERE Maps JS API v3.1 wrapper. The HERE JS SDK is NOT an npm package — it
// loads via <script> tags (CLAUDE.md notes). We inject the SDK scripts on
// mount and re-use the global `window.H` namespace.
//
// Marker rendering uses H.clustering.Provider (eps:64, minWeight:2 per UI-SPEC).
// Each trip becomes a marker; color is derived client-side via getMarkerColor
// (DIS-15 client-side FSM) and synced with TRIP_STATUS_COLORS tokens so the
// map color and AlertToast severity always agree.

import { useEffect, useMemo, useRef } from "react";

import { type DashboardTrip, getMarkerColor, isTripDelayed } from "../api/dashboard";
import { DELAY_OVERLAY, TRIP_STATUS_COLORS, type TripStatusColor } from "../lib/tokens";

// WR-14 known risk (TRACKED FOR P1): scripts loaded from js.api.here.com
// without SRI hashes. A here.com supply-chain compromise would execute
// arbitrary JS in the Mini App origin with full access to localStorage
// (incl. sd:jwt). CSP nonces don't help — externally-loaded scripts
// bypass nonces.
//
// SRI on HERE is operationally fragile: HERE breaks SRI on minor version
// bumps (3.1.x → 3.1.y), so pinning hashes turns into a recurring
// maintenance task that delays security patches.
//
// Mitigation plan for P1 (NOT IN MVP scope): mirror the HERE Maps SDK to
// Hetzner Object Storage under track.silentdispatcher.com/static/here/
// and serve from a single-origin path. The same-origin script then
// honours CSP nonces. CI workflow refreshes the mirror weekly.
//
// For MVP we accept the risk and document it here so future audits can
// surface it without re-reviewing all third-party script tags.
const HERE_BASE = "https://js.api.here.com/v3/3.1";
const HERE_SCRIPTS = [
  "mapsjs-core.js",
  "mapsjs-service.js",
  "mapsjs-ui.js",
  "mapsjs-mapevents.js",
  "mapsjs-clustering.js",
];
const HERE_UI_CSS = `${HERE_BASE}/mapsjs-ui.css`;

declare global {
  interface Window {
    H?: any; // HERE Maps JS API namespace
  }
}

export interface DashboardMapProps {
  trips: DashboardTrip[];
  /** HERE Maps freemium-tier API key (from env via Vite). */
  apiKey?: string;
  /** Optional click handler for individual trip markers. */
  onTripClick?: (tripId: number) => void;
  /** Now timestamp — passed through to getMarkerColor for deterministic tests. */
  now?: Date;
}

// ---------------------------------------------------------------------------
// Marker SVG builder — 24x24 circle with color from TRIP_STATUS_COLORS.
// ---------------------------------------------------------------------------

// R1 delay overlay: when `delayed`, draw a gold corner-dot ON TOP of the base-color
// marker (UI-SPEC §5). The base status fill is unchanged — the gold dot is a separate
// layer, mirroring Onfleet's gold delay-dot in the corner of a status pin.
function buildMarkerSvg(color: TripStatusColor, delayed = false): string {
  const fill = TRIP_STATUS_COLORS[color].fill;
  const base = `<circle cx="14" cy="14" r="11" fill="${fill}" stroke="white" stroke-width="3"/><text x="14" y="18" text-anchor="middle" fill="white" font-size="13" font-weight="bold">T</text>`;
  const goldDot = delayed
    ? `<circle cx="22" cy="6" r="5" fill="${DELAY_OVERLAY.fill}" stroke="${DELAY_OVERLAY.outline}" stroke-width="2"/>`
    : "";
  return `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">${base}${goldDot}</svg>`;
}

// ---------------------------------------------------------------------------
// HERE SDK loader — idempotent. Resolves once all 5 scripts are loaded.
// ---------------------------------------------------------------------------

let _loadPromise: Promise<void> | null = null;

function loadHereSdk(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }
  if (window.H) {
    return Promise.resolve();
  }
  if (_loadPromise) {
    return _loadPromise;
  }
  _loadPromise = (async () => {
    // Load UI CSS once.
    if (!document.querySelector(`link[href="${HERE_UI_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = HERE_UI_CSS;
      document.head.appendChild(link);
    }
    // Sequential script loads — HERE modules depend on each other (core first).
    for (const name of HERE_SCRIPTS) {
      const src = `${HERE_BASE}/${name}`;
      if (document.querySelector(`script[src="${src}"]`)) {
        continue;
      }
      await new Promise<void>((resolve, reject) => {
        const s = document.createElement("script");
        s.src = src;
        s.async = false;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(s);
      });
    }
  })();
  return _loadPromise;
}

export function DashboardMap({
  trips,
  apiKey,
  onTripClick,
  now,
}: DashboardMapProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const clusterProviderRef = useRef<any>(null);

  // Compute trip → marker color mapping once per render.
  const tripsWithColor = useMemo(
    () =>
      trips
        .filter((t) => t.last_gps !== null)
        .map((t) => ({
          trip: t,
          color: getMarkerColor(t, now),
          delayed: isTripDelayed(t, now),
        })),
    [trips, now],
  );

  // Init map once (apiKey + container available).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadHereSdk();
      } catch (err) {
        console.error("DashboardMap: HERE SDK load failed", err);
        return;
      }
      if (cancelled || !containerRef.current || !window.H || !apiKey) return;
      if (mapRef.current) return; // already initialized

      const platform = new window.H.service.Platform({ apikey: apiKey });
      const defaultLayers = platform.createDefaultLayers();
      const map = new window.H.Map(containerRef.current, defaultLayers.vector.normal.map, {
        zoom: 4,
        center: { lat: 49.0, lng: 16.0 }, // Central Europe — covers EU truck routes.
        pixelRatio: window.devicePixelRatio || 1,
      });
      // HERE pattern: instantiate Behavior + UI; both attach to the map
      // via side effects (constructor wires the event listeners). We do not
      // need the instance references — but we MUST construct them.
      new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(map));
      window.H.ui.UI.createDefault(map, defaultLayers);
      mapRef.current = map;
    })();
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // Update cluster provider whenever trips change.
  useEffect(() => {
    if (!mapRef.current || !window.H?.clustering) return;

    // Remove previous cluster provider.
    if (clusterProviderRef.current) {
      try {
        mapRef.current.removeLayer(clusterProviderRef.current);
      } catch {
        // ignore
      }
      clusterProviderRef.current = null;
    }

    if (tripsWithColor.length === 0) return;

    const dataPoints = tripsWithColor.map(({ trip, color, delayed }) => {
      const lat = trip.last_gps?.lat;
      const lng = trip.last_gps?.lng;
      const point = new window.H.clustering.DataPoint(lat, lng, 1, { trip, color, delayed });
      return point;
    });

    const provider = new window.H.clustering.Provider(dataPoints, {
      clusteringOptions: { eps: 64, minWeight: 2 },
      theme: {
        getClusterPresentation: (cluster: any) => {
          // Worst-severity rule per UI-SPEC §Color. Delay (R1) is a SEPARATE
          // overlay: the cluster shows the gold dot if ANY member is delayed,
          // independent of the worst base color.
          let worst: TripStatusColor = "green";
          let anyDelayed = false;
          cluster.forEachDataPoint((dp: any) => {
            const c: TripStatusColor = dp.getData()?.color ?? "green";
            if (c === "red") worst = "red";
            else if (c === "yellow" && worst !== "red") worst = "yellow";
            if (dp.getData()?.delayed) anyDelayed = true;
          });
          const icon = new window.H.map.Icon(buildMarkerSvg(worst, anyDelayed));
          const m = new window.H.map.Marker(cluster.getPosition(), { icon });
          return m;
        },
        getNoisePresentation: (noisePoint: any) => {
          const data = noisePoint.getData();
          const color: TripStatusColor = data?.color ?? "green";
          const delayed: boolean = data?.delayed === true;
          const icon = new window.H.map.Icon(buildMarkerSvg(color, delayed));
          const m = new window.H.map.Marker(noisePoint.getPosition(), {
            icon,
            data,
          });
          if (onTripClick) {
            m.addEventListener("tap", () => {
              const trip = data?.trip as DashboardTrip | undefined;
              if (trip) onTripClick(trip.id);
            });
          }
          return m;
        },
      },
    });
    mapRef.current.addLayer(provider);
    clusterProviderRef.current = provider;
  }, [tripsWithColor, onTripClick]);

  return (
    <div
      ref={containerRef}
      data-testid="dashboard-map"
      data-trip-count={String(tripsWithColor.length)}
      role="application"
      aria-label="Dashboard live map"
      style={{
        width: "100%",
        height: 320,
        borderRadius: 12,
        background: "#E2E8F0",
        overflow: "hidden",
      }}
    >
      {/* Map renders inside this div via HERE H.Map mount. */}
    </div>
  );
}
